import type { CSSProperties, ReactElement, ReactNode, Ref } from 'react';
import { Fragment, memo } from 'react';

import type {
  ColorProps,
  HeightProps,
  MaxHeightProps,
  MinHeightProps,
  SpaceProps,
} from 'styled-system';

import { Placeholder } from './components/Placeholder';
import { SortedIndicator } from './components/SortedIndicator';
import {
  DataCell,
  HeaderCell,
  Table,
  TableContainer,
  TableHead,
  TableLoading,
  TableRow,
  TruncateSpan,
} from './components/table-styled-components';
import type {
  IDataTableColumn,
  IDataTableColumnHeader,
  ISortedTable,
  ITableSettings,
  OverwriteRowPropsType,
  OverwriteRowType,
} from './types';
import { getDeep, getStickyProps } from './utils';

const OVERWRITE_INITIAL_VALUE: OverwriteRowPropsType = {
  isRowHide: false,
  rowStyle: {},
};

const SETTINGS_TABLE_INITIAL_VALUE: ITableSettings = {
  hideHeader: false,
  horizontalLine: true,
  backlightLine: true,
};

function getHeaderProps(
  header: string | IDataTableColumnHeader | undefined,
): IDataTableColumnHeader {
  if (typeof header === 'undefined') return { text: '' };
  if (typeof header === 'string') return { text: header };
  return header;
}

interface ColumnsDataProps {
  item: unknown;
  index: number;
  columns: IDataTableColumn[];
  rowHeight?: HeightProps['height'];
  backlightLine?: boolean;
  rowKey?: string;
}

const ColumnsData = memo(function ColumnsData({
  item,
  index,
  columns,
  rowHeight,
  rowKey,
  backlightLine,
}: ColumnsDataProps) {
  return (
    <Fragment>
      {columns.map(({
        key,
        render,
        truncate = true,
        colSpan,
        fixedWidth,
        cellStyle,
        sticky,
        visibleByHover,
        highlightColumn,
      }) => {
        const value = getDeep(item, key);
        const renderValue =
          typeof render === 'function' ? render(value, item, index) : (value as ReactNode);

        const stickyProps = getStickyProps(sticky);

        return (
          <DataCell
            key={`${rowKey}_${key}`}
            $backlightLine={backlightLine}
            colSpan={colSpan}
            data-sticky={!!sticky}
            $defaultHeight={rowHeight}
            $fixedWidth={fixedWidth}
            $highlightColumn={highlightColumn}
            $sticky={!!sticky}
            $truncate={truncate}
            $visibleByHover={visibleByHover}
            {...stickyProps}
            {...(cellStyle as object)}
          >
            {truncate ? <TruncateSpan>{renderValue}</TruncateSpan> : renderValue}
          </DataCell>
        );
      })}
    </Fragment>
  );
});

const defaultRowKey = (item: unknown, index: number): string => {
  if (item !== null && typeof item === 'object' && 'id' in item) {
    return String((item as Record<string, unknown>).id ?? index);
  }
  return String(index);
};

export interface DataTableProps<Item = unknown> {
  ref?: Ref<HTMLDivElement>;
  id?: string;
  /**
   * Задаёт borderRadius контейнеру таблицы.
   */
  radius?: boolean;
  /**
   * Задаёт бордер родительскому контейнеру.
   */
  border?: boolean;
  /**
   * Настройка визуального отображения таблицы.
   */
  settings?: ITableSettings;
  className?: string;
  style?: CSSProperties;
  /**
   * Индикатор загрузки (прогресс-бар под шапкой).
   */
  loading?: boolean;
  /**
   * Описание колонок таблицы.
   */
  columns: IDataTableColumn<Item>[];
  /**
   * Функция для получения уникального ключа строки.
   */
  getRowKey?: (item: Item, index?: number) => string;
  /**
   * Позволяет скрывать или стилизовать отдельные строки.
   * isRowHide — скрыть строку визуально, сохранив в массиве данных.
   * rowStyle — задать стили строки.
   */
  overwriteRow?: OverwriteRowType<Item>;
  /**
   * Данные таблицы.
   */
  data: Item[];
  /**
   * Плейсхолдер при отсутствии данных.
   */
  placeholder?: { text?: ReactNode };
  /**
   * Нижняя область таблицы: передайте строки (`<tr>`), обёрнутые во фрагмент при необходимости.
   * Рендерится внутри `<tfoot>` после `<tbody>`.
   */
  footer?: ReactNode;
  /**
   * Включает mobile-адаптацию таблицы на узких экранах.
   */
  mobileCollapse?: boolean;
}

function DataTable<T = unknown>({
  ref,
  radius,
  border,
  settings = SETTINGS_TABLE_INITIAL_VALUE,
  className,
  style,
  orderBy,
  orderDirection,
  onSort,
  loading,
  data,
  columns,
  getRowKey = defaultRowKey as (item: T, index?: number) => string,
  placeholder,
  footer,
  mobileCollapse = true,
  overwriteRow,
  ...other
}: DataTableProps<T> &
  ISortedTable &
  SpaceProps &
  MinHeightProps &
  HeightProps &
  MaxHeightProps &
  ColorProps) {
  const renderHeaderCell = ({
    key,
    header,
    sortable = true,
    cellStyle = {},
    sticky,
    visibleByHover,
  }: IDataTableColumn): ReactElement => {
    const { text, fixedWidth, headerCellStyle, ...props } = getHeaderProps(header);

    const headerContent = fixedWidth ? <TruncateSpan>{text}</TruncateSpan> : text;
    const stickyProps = getStickyProps(sticky);

    return (
      <HeaderCell
        key={key}
        $fixedWidth={fixedWidth}
        height={settings.headerRowHeight}
        $sticky={!!sticky}
        $striped={settings.striped}
        $visibleByHover={visibleByHover}
        {...stickyProps}
        {...((headerCellStyle ?? cellStyle) as object)}
        {...(props as object)}
      >
        {sortable ? (
          <SortedIndicator
            columnKey={key}
            orderBy={orderBy}
            orderDirection={orderDirection}
            onSort={onSort}
          >
            {headerContent}
          </SortedIndicator>
        ) : (
          headerContent
        )}
      </HeaderCell>
    );
  };

  const renderRow = (item: T, index: number): ReactElement | null => {
    const rowKey = getRowKey(item, index);

    const { isRowHide, rowStyle } = overwriteRow
      ? (overwriteRow(item, index) ?? OVERWRITE_INITIAL_VALUE)
      : OVERWRITE_INITIAL_VALUE;

    if (isRowHide) return null;

    return (
      <TableRow
        key={`${rowKey}-${index}`}
        $backlightLine={settings.backlightLine}
        $horizontalLine={settings.horizontalLine}
        $striped={settings.striped}
        $verticalLine={settings.verticalLine}
        {...(rowStyle as object)}
      >
        <ColumnsData
          backlightLine={settings.backlightLine}
          columns={columns as IDataTableColumn[]}
          index={index}
          item={item}
          rowHeight={settings.rowHeight}
          rowKey={rowKey}
        />
      </TableRow>
    );
  };

  const noData = data.length === 0 && !loading;

  return (
    <TableContainer
      ref={ref}
      $border={border}
      className={className}
      data-mobile-collapse={mobileCollapse}
      $mobileCollapse={mobileCollapse}
      $radius={radius}
      style={style}
      {...other}
    >
      <Table>
        {!settings?.hideHeader && (
          <TableHead>
            <tr>{(columns as IDataTableColumn[]).map(renderHeaderCell)}</tr>
          </TableHead>
        )}
        {!noData && <tbody>{data.map(renderRow)}</tbody>}
        {footer != null && <tfoot>{footer}</tfoot>}
      </Table>
      {noData && <Placeholder text={placeholder?.text} />}
      {loading && <TableLoading />}
    </TableContainer>
  );
}

export { DataTable };
